/**
 * Hero Products Intro — Cinematic animation sequencer
 * Timeline: type → reveal scratched → draw scribble → reveal gold banner → CTA
 * No dependencies — vanilla JS + IntersectionObserver for scroll trigger.
 */
(function () {
  'use strict';

  var TYPING_SPEED = 70;     // ms per character
  var PAUSE_AFTER_TYPE = 400; // ms before next line
  var SCRATCH_DELAY = 300;    // ms after scratched text appears
  var BANNER_DELAY = 500;     // ms after scratch finishes
  var IMAGES_DELAY = 350;     // ms after banner before images appear
  var IMAGE_STAGGER = 180;    // ms between each image fade-in
  var CTA_DELAY = 400;        // ms after banner/images appears

  /**
   * Typewriter — writes characters into an element one at a time.
   * @param {HTMLElement} el  - target element
   * @param {string} text     - string to type
   * @param {number} speed    - ms between characters
   * @param {Function} onDone - callback when finished
   */
  function typewrite(el, text, speed, onDone) {
    var i = 0;
    var safeText = text || '';
    el.textContent = '';

    function tick() {
      if (i < safeText.length) {
        el.textContent += safeText.charAt(i);
        i++;
        setTimeout(tick, speed);
      } else if (onDone) {
        onDone();
      }
    }
    tick();
  }

  /**
   * Reveal a line by adding is-visible class.
   */
  function showLine(el, cb, delay) {
    setTimeout(function () {
      el.classList.add('is-visible');
      if (cb) cb();
    }, delay || 0);
  }

  /**
   * Trigger SVG scratch drawing by adding is-drawing to each path.
   */
  function drawScratches(container, cb) {
    var paths = container.querySelectorAll('.hpi__scratch');
    paths.forEach(function (path) {
      path.classList.add('is-drawing');
    });
    // After both strokes finish (main 0s + 0.55s, cross 0.28s + 0.38s = 0.66s),
    // mark the line as struck so the word dims subtly.
    setTimeout(function () {
      container.classList.add('is-struck');
    }, 700);
    setTimeout(function () {
      if (cb) cb();
    }, 900);
  }

  /**
   * Orchestrate the full animation sequence for one section instance.
   */
  function animateSection(section) {
    if (section.dataset.hpiAnimated) return;
    section.dataset.hpiAnimated = 'true';

    var lineTyped    = section.querySelector('.hpi__line--typed');
    var lineScratch  = section.querySelector('.hpi__line--scratched');
    var lineBanner   = section.querySelector('.hpi__line--banner');
    var lineImages   = section.querySelector('.hpi__line--images');
    var lineCta      = section.querySelector('.hpi__line--cta');
    var typedOutput  = section.querySelector('.hpi__typed-output');
    var cursor       = section.querySelector('.hpi__cursor');
    var text         = typedOutput ? typedOutput.getAttribute('data-typed-text') : '';

    function revealAfterBanner() {
      if (lineImages) {
        setTimeout(function () {
          showLine(lineImages, function () {
            // Stagger individual image items for a cinematic reveal
            var items = lineImages.querySelectorAll('.hpi__image-item');
            items.forEach(function (item, idx) {
              setTimeout(function () { item.classList.add('is-visible'); }, idx * IMAGE_STAGGER);
            });
            if (lineCta) {
              showLine(lineCta, null, CTA_DELAY + items.length * IMAGE_STAGGER);
            }
          });
        }, IMAGES_DELAY);
      } else if (lineCta) {
        showLine(lineCta, null, CTA_DELAY);
      }
    }

    function showBanner() {
      setTimeout(function () {
        showLine(lineBanner, revealAfterBanner);
      }, BANNER_DELAY);
    }

    // Step 1 — reveal typed line, begin typewriter
    showLine(lineTyped, function () {
      typewrite(typedOutput, text, TYPING_SPEED, function () {
        // Hide cursor after typing
        if (cursor) {
          setTimeout(function () { cursor.classList.add('is-hidden'); }, 600);
        }

        if (lineScratch) {
          // Step 2 — reveal scratched text → draw scribble → banner
          setTimeout(function () {
            showLine(lineScratch, function () {
              setTimeout(function () {
                drawScratches(lineScratch, showBanner);
              }, SCRATCH_DELAY);
            });
          }, PAUSE_AFTER_TYPE);
        } else {
          // No scratched line — go straight from typed to banner
          setTimeout(showBanner, PAUSE_AFTER_TYPE);
        }
      });
    }, 0);
  }

  /**
   * Use IntersectionObserver so the animation only fires when scrolled into view.
   */
  function init() {
    var sections = document.querySelectorAll('.hero-products-intro');
    if (!sections.length) return;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateSection(entry.target);
            observer.unobserve(entry.target);
          }
        });
      }, { threshold: 0.25 });

      sections.forEach(function (s) { observer.observe(s); });
    } else {
      // Fallback — animate immediately
      sections.forEach(animateSection);
    }
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
